import {
  AccountInfo,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  Configuration,
  createNestablePublicClientApplication,
  createStandardPublicClientApplication,
  IPublicClientApplication,
} from '@azure/msal-browser';
import * as Sentry from '@sentry/react';
import { azureClientId } from '../env-variables.config';

export const GRAPH_SCOPES = ['Mail.ReadWrite', 'MailboxSettings.ReadWrite', 'Calendars.ReadWrite'];

const UNAUTHORIZED = 401;

const buildMsalConfig = (): Configuration => ({
  auth: {
    clientId: azureClientId,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: globalThis.location.origin,
  },
  cache: {
    // The taskpane is torn down and recreated every time it is reopened. A persistent cache keeps
    // the refresh token around so access tokens can be renewed without prompting the user again.
    cacheLocation: 'localStorage',
  },
});

const isNestedAppAuthSupported = (): boolean =>
  Office.context?.requirements?.isSetSupported('NestedAppAuth', '1.1') ?? false;

let nestedClientInitialization: Promise<IPublicClientApplication> | undefined;
let standardClientInitialization: Promise<IPublicClientApplication> | undefined;
let isNestedClientRejected = false;

/**
 * Hosts that support nested app authentication hand out Graph tokens through their own signed-in
 * account, so renewal never depends on the 24-hour refresh token Entra issues to single-page apps or
 * on a hidden sign-in iframe the taskpane cannot complete. Undefined when the host lacks support or
 * nested auth already failed in this taskpane where standard MSAL succeeded.
 */
const getNestedClient = (): Promise<IPublicClientApplication> | undefined => {
  if (isNestedClientRejected || !isNestedAppAuthSupported()) return undefined;
  nestedClientInitialization ??= createNestablePublicClientApplication(buildMsalConfig());
  return nestedClientInitialization;
};

// Built on first use so that importing this module never touches the browser environment.
const getStandardClient = (): Promise<IPublicClientApplication> => {
  standardClientInitialization ??= createStandardPublicClientApplication(buildMsalConfig());
  return standardClientInitialization;
};

type GraphAuthClient = 'nested' | 'standard';

const errorCodeOf = (error: unknown): string =>
  (error as { errorCode?: string } | undefined)?.errorCode ?? 'unknown';

const reportedGraphAuthFailures = new Set<string>();

/**
 * Records why a client failed to produce a token, grouped per client and MSAL error code, so it is
 * visible which hosts fall back from nested auth and why sessions end. Once acquisition fails, every
 * mail selection fails the same way until the user reauthorizes, so each pair is reported once.
 */
const reportGraphAuthFailure = (client: GraphAuthClient, error: unknown) => {
  const msalErrorCode = errorCodeOf(error);
  const failureKey = `${client}:${msalErrorCode}`;
  if (reportedGraphAuthFailures.has(failureKey)) return;
  reportedGraphAuthFailures.add(failureKey);
  Sentry.captureException(error, {
    tags: {
      graphAuthClient: client,
      msalErrorCode,
      nestedAppAuthSupported: String(isNestedAppAuthSupported()),
    },
    fingerprint: ['graph-auth-failure', client, msalErrorCode],
  });
};

const getLoginHint = () => Office.context?.mailbox?.userProfile?.emailAddress;

const isInteractionInProgressError = (error: unknown): boolean =>
  error instanceof BrowserAuthError &&
  error.errorCode === BrowserAuthErrorCodes.interactionInProgress;

// The fixed cache key MSAL uses for its "interaction in progress" flag (`msal.<clientId>` is only
// used for account/token keys, not this one — see BrowserCacheManager.getInteractionInProgress).
const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

/**
 * The Office taskpane can be torn down mid-popup (closed, reloaded, or replaced) whenever it is
 * reopened, which strands MSAL's "interaction in progress" flag from that earlier instance. MSAL
 * then refuses every future popup with `interaction_in_progress` forever, even though nothing is
 * actually running. Clearing the flag lets the current instance retry instead of staying stuck.
 *
 * Only safe because `acquireExclusive` never starts a second interaction while this instance has
 * one open, so a collision here can only come from a stranded flag, never from a live popup.
 */
const clearStuckInteractionStatus = () => {
  sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
  localStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
};

const withInteractionRecovery = async <T>(startInteraction: () => Promise<T>): Promise<T> => {
  try {
    return await startInteraction();
  } catch (error) {
    if (!isInteractionInProgressError(error)) throw error;
    clearStuckInteractionStatus();
    return startInteraction();
  }
};

const signInSilently = async (msal: IPublicClientApplication): Promise<AccountInfo> => {
  const { account } = await msal.ssoSilent({
    scopes: GRAPH_SCOPES,
    loginHint: getLoginHint(),
  });
  return account;
};

const resolveActiveAccount = async (msal: IPublicClientApplication): Promise<AccountInfo> => {
  const activeAccount = msal.getActiveAccount();
  if (activeAccount) return activeAccount;

  const account = msal.getAllAccounts()[0] ?? (await signInSilently(msal));
  msal.setActiveAccount(account);
  return account;
};

type GraphAuthStatusListener = (needsReauth: boolean) => void;

let needsGraphReauth = false;
const graphAuthStatusListeners = new Set<GraphAuthStatusListener>();

const setNeedsGraphReauth = (value: boolean) => {
  if (needsGraphReauth === value) return;
  needsGraphReauth = value;
  graphAuthStatusListeners.forEach((listener) => listener(value));
};

/**
 * Lets UI react when Graph access needs the user's attention: an admin revoked or never granted
 * consent for the required scopes, or an interactive sign-in popup is currently pending (set true
 * before the popup opens, not just on failure, so an operation blocked behind a stuck/backgrounded
 * popup surfaces to the user instead of spinning silently). Clears once a sign-in succeeds.
 */
export const subscribeToGraphAuthStatus = (listener: GraphAuthStatusListener): (() => void) => {
  listener(needsGraphReauth);
  graphAuthStatusListeners.add(listener);
  return () => {
    graphAuthStatusListeners.delete(listener);
  };
};

const promptForGraphAccess = async (msal: IPublicClientApplication): Promise<string> => {
  setNeedsGraphReauth(true);
  const result = await withInteractionRecovery(() =>
    msal.acquireTokenPopup({
      scopes: GRAPH_SCOPES,
      loginHint: getLoginHint(),
    }),
  );
  msal.setActiveAccount(result.account);
  setNeedsGraphReauth(false);
  return result.accessToken;
};

type GraphTokenRequest = {
  /** Renew from the refresh token instead of serving MSAL's cached access token. */
  forceRefresh: boolean;
  /** Prompt the user instead of attempting a silent acquisition first. */
  interactive: boolean;
};

const acquireTokenSilently = async (
  msal: IPublicClientApplication,
  forceRefresh: boolean,
): Promise<string> => {
  const account = await resolveActiveAccount(msal);
  const { accessToken } = await msal.acquireTokenSilent({
    scopes: GRAPH_SCOPES,
    account,
    forceRefresh,
  });
  return accessToken;
};

/**
 * Nested auth depends on the Outlook build and on the app registration listing the broker redirect
 * URI, so it can fail where standard MSAL still works. Standard MSAL is tried before the user sees
 * anything, and once it succeeds where nested failed, nested is skipped for the rest of this
 * taskpane so later requests do not pay for a failing attempt first.
 */
const acquireTokenSilentlyWithFallback = async (forceRefresh: boolean): Promise<string> => {
  const nestedClient = getNestedClient();
  if (!nestedClient) return acquireTokenSilently(await getStandardClient(), forceRefresh);

  try {
    return await acquireTokenSilently(await nestedClient, forceRefresh);
  } catch (nestedError) {
    reportGraphAuthFailure('nested', nestedError);
    const accessToken = await acquireTokenSilently(await getStandardClient(), forceRefresh);
    isNestedClientRejected = true;
    return accessToken;
  }
};

const acquireGraphAccessToken = async ({
  forceRefresh,
  interactive,
}: GraphTokenRequest): Promise<string> => {
  try {
    /*
     * The browser only opens a popup while it still credits the click that asked for it, so a
     * reauthorization the user requested goes straight to the prompt. Attempting a silent
     * acquisition first would spend that credit on a renewal we already know has failed — MSAL can
     * sit on a blocked renewal iframe for ten seconds — and the popup would then be suppressed.
     *
     * The prompt always uses standard MSAL: if nested auth were misconfigured, a nested prompt
     * could fail after showing the user an error and then need a second prompt. Consent granted
     * through the standard popup covers the same app, so nested renewal works afterwards.
     */
    const accessToken = interactive
      ? await promptForGraphAccess(await getStandardClient())
      : await acquireTokenSilentlyWithFallback(forceRefresh);
    setNeedsGraphReauth(false);
    return accessToken;
  } catch (error) {
    setNeedsGraphReauth(true);
    if (!interactive) reportGraphAuthFailure('standard', error);
    throw error;
  }
};

let pendingTokenRequest: Promise<string> | undefined;
let pendingInteraction: Promise<string> | undefined;

/**
 * MSAL allows only one interactive popup at a time; a second one throws `interaction_in_progress`
 * instead of queueing. Routing acquisitions through this single in-flight promise means a forced
 * refresh never races an acquisition already under way, and a plain read reuses its result.
 *
 * An interactive request pre-empts a silent one instead of queueing. Waiting would push
 * `acquireTokenPopup` past the click that asked for it — a silent renewal can hold the slot for the
 * whole iframe timeout — and the browser would then suppress the popup. While a popup is open,
 * every request joins it, so a repeated click never opens a second sign-in window.
 */
const acquireExclusive = (request: GraphTokenRequest): Promise<string> => {
  if (pendingInteraction) return pendingInteraction;

  if (pendingTokenRequest && !request.interactive) {
    return request.forceRefresh
      ? pendingTokenRequest.catch(() => undefined).then(() => acquireExclusive(request))
      : pendingTokenRequest;
  }

  const tokenRequest = acquireGraphAccessToken(request);
  pendingTokenRequest = tokenRequest;
  if (request.interactive) pendingInteraction = tokenRequest;
  // Only the request that still owns the slot may release it, so a pre-empted one settling later
  // cannot strand the interaction that replaced it.
  const releaseSlot = () => {
    if (pendingTokenRequest === tokenRequest) pendingTokenRequest = undefined;
    if (pendingInteraction === tokenRequest) pendingInteraction = undefined;
  };
  tokenRequest.then(releaseSlot, releaseSlot);
  return tokenRequest;
};

/**
 * Returns a Graph access token that is valid right now. MSAL serves it from its cache and renews it
 * with the refresh token when it is expired or about to expire, so the token is never read from a
 * place where it can go stale.
 *
 * Never opens a sign-in popup: callers run on mail selection and other events the user did not
 * start about signing in, and classic Outlook turns every popup into a new window. When the token
 * cannot be renewed silently this rejects and raises the reauthorization banner, and only
 * `reauthorizeGraphAccess` — triggered by the user — prompts.
 */
export const getGraphAccessToken = async ({ forceRefresh = false } = {}): Promise<string> =>
  acquireExclusive({ forceRefresh, interactive: false });

/**
 * Re-grants Graph access from a prompt the user asked for, skipping the silent renewal that brought
 * the add-in here. Resolves with a usable token so a caller can carry on with the work that was
 * blocked, and clears the reauthorization banner on the way.
 */
export const reauthorizeGraphAccess = (): Promise<string> =>
  acquireExclusive({ forceRefresh: true, interactive: true });

export type GraphReauthorizationFailure = 'cancelled' | 'popupBlocked' | 'failed';

const POPUP_UNAVAILABLE_FAILURES: ReadonlySet<string> = new Set([
  BrowserAuthErrorCodes.popupWindowError,
  BrowserAuthErrorCodes.emptyWindowError,
]);

/**
 * Says why an interactive reauthorization produced no token, so the UI can tell the user what
 * happened. Without it a dismissed popup, a popup the browser refused to open and a refused consent
 * all look identical from the outside: the spinner stops and the banner stays.
 */
export const classifyReauthorizationFailure = (error: unknown): GraphReauthorizationFailure => {
  if (!(error instanceof BrowserAuthError)) return 'failed';
  if (error.errorCode === BrowserAuthErrorCodes.userCancelled) return 'cancelled';
  return POPUP_UNAVAILABLE_FAILURES.has(error.errorCode) ? 'popupBlocked' : 'failed';
};

const withBearerToken = (init: RequestInit, accessToken: string): RequestInit => {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  return { ...init, headers };
};

/**
 * Calls Microsoft Graph with a freshly resolved token. If Graph still rejects the token — it can
 * expire between acquiring it and the request reaching the service — the token is force-refreshed
 * and the call is retried once.
 */
export const graphFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const response = await fetch(url, withBearerToken(init, await getGraphAccessToken()));
  if (response.status !== UNAUTHORIZED) return response;

  const refreshedToken = await getGraphAccessToken({ forceRefresh: true });
  return fetch(url, withBearerToken(init, refreshedToken));
};
